import { Alert } from '@interstate/components/Alert';
import type { AlertSeverity } from '@interstate/components/Alert/Types/alertTypes';
import { Typography } from '@interstate/components/Typography';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import * as accessoriesActionTypes from '@makemydeal/dr-dash-store/actionTypes/accessoriesActionTypes';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as tradeActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as psErrorUtils from '@makemydeal/dr-dash-store/utils/psErrorUtils';
import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

import type { IPaymentTermMessage } from '@makemydeal/dr-platform-shared';
import { BALLOON, FINANCE, LEASE, CASH } from '@makemydeal/dr-platform-types';
import type { IPaymentStatusProps } from './types/paymentStatusProps';
import { isEqual } from 'lodash';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { useCallback } from 'react';
import {
    PS_ERROR_MESSAGE_MAP,
    PS_ERROR_PASS_THROUGH_KEYS,
    PS_ERROR_MAP_BY_CALC_ENDPOINT,
    PS_ERROR_MAP_PROGRAM_QUOTES_ASP_HIDDEN,
    BALLOON_PS_ERROR_MESSAGE_MAP,
    NEGATIVE_AMORTIZATION_PS_ERROR_MESSAGE_MAP
} from './constants/psErrorMessageMap';
import { getPSErrorFallbackMessage, parseMessageWithTitle } from './utils/paymentStatusUtil';
import { getUPQMissingFieldsMessage } from './utils/paymentStatusHelpers';
import { useUPQRequiredFieldsValidation } from '../hooks';

const PaymentStatusInterstate = ({ testId, sx: sxOverrides }: IPaymentStatusProps) => {
    const isPaymentError = useSelector(paymentSelectors.getIsError);
    let paymentMessages = useSelector(offerReduxSelectors.getMessagesFromCurrentTerm, isEqual);
    const isGapCappedByLender = useSelector(vehicleProtectionSelectors.getIsGapCappedByLender);
    const isPriorBalanceEnabled = useSelector(featureToggleSelectors.isPriorBalanceEnabled);
    const hasPriorLeaseBalance = useSelector(offerReduxSelectors.hasPriorLeaseBalance);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const netCashPayout = useSelector(offerReduxSelectors.getNetCashPayout);
    const customMessage = [{ message: PS_ERROR_MESSAGE_MAP.gap_adjusted_by_lender, messageType: 'error' }];
    const customMessageForPositiveNetCashPayout = [
        ...(currentOfferType === FINANCE || currentOfferType === LEASE
            ? [
                  {
                      message: PS_ERROR_MESSAGE_MAP.excesstradein_equitywillbereturnedtothecustomer.replace(
                          '{}',
                          currentOfferType === FINANCE ? 'Financing' : 'Leasing'
                      ),
                      messageType: 'error'
                  }
              ]
            : []),
        ...(currentOfferType === CASH
            ? [
                  {
                      message: PS_ERROR_MESSAGE_MAP.excesstradein_equitywillbereturnedtothecustomer_cash,
                      messageType: 'error'
                  }
              ]
            : [])
    ];
    paymentMessages = [
        ...paymentMessages,
        ...(isGapCappedByLender ? customMessage : []),
        ...(netCashPayout > 0 ? customMessageForPositiveNetCashPayout : [])
    ];
    const shouldSurfacePriorBalanceNotices = isPriorBalanceEnabled && hasPriorLeaseBalance;
    const displayableMessageTypes = shouldSurfacePriorBalanceNotices ? ['error', 'notice'] : ['error'];
    const hasMessageType = (message: IPaymentTermMessage, type: string) =>
        Boolean(message.messageType?.toLowerCase().includes(type));
    const isDisplayableMessage = (message: IPaymentTermMessage) =>
        displayableMessageTypes.some((type) => hasMessageType(message, type));
    const isErrorMessage = (message: IPaymentTermMessage) => hasMessageType(message, 'error');
    const paymentHasMessages = Boolean(paymentMessages && paymentMessages.length && paymentMessages.some(isDisplayableMessage));
    const displayedMessages = shouldSurfacePriorBalanceNotices ? paymentMessages.filter(isDisplayableMessage) : paymentMessages;
    const showMultipleErrorsTitle = shouldSurfacePriorBalanceNotices
        ? displayedMessages.length > 1 && displayedMessages.some(isErrorMessage)
        : paymentMessages.length > 1;
    const failedAllowance = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.ALLOWANCE_CHANGED));
    const failedPayoff = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.PAYOFF_CHANGED));
    const failedMenuAccessories = useSelector(
        deskingSelectors.wasTheLastFailure(accessoriesActionTypes.UPDATED_OFFER_MENU_PRODUCTS)
    );
    const failedManualIncentive = useSelector(deskingSelectors.wasTheLastFailure(offerReduxActionTypes.MODIFY_MANUAL_INCENTIVE));
    const hasSelectedProgram = useSelector(programsSelectors.getHasSelectedProgram);
    const isNoProgramIncentiveMessageEnabled = useSelector(featureToggleSelectors.isNoProgramIncentiveMessageEnabled);
    const currentCalcEndpoint = useSelector((state: any) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, currentOfferType)
    );
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);

    const psErrorDetails = useSelector(deskingSelectors.getPSErrorDetails);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealerId = useSelector(dealerSelectors.getDealerId);

    const { missingFields, hasMissingRequiredFields, lastPaymentMissingUPQFields } =
        useUPQRequiredFieldsValidation(currentOfferType);
    const upqMissingFieldsMessage = useMemo(() => {
        if (!hasMissingRequiredFields) return null;
        return getUPQMissingFieldsMessage(missingFields);
    }, [hasMissingRequiredFields, missingFields, currentOfferType]);

    const logPSErrorToNR = useCallback(
        (name: string, message: string, mappingExists: boolean) => {
            const action = {
                message,
                dealId: dealXgId,
                dealerID: dealerId,
                actionForPayment: psErrorDetails.actionForPayment,
                originalPSError: psErrorDetails.originalPSError,
                mappingExists,
                psErrorMessagePath: psErrorDetails.psErrorMessagePath
            };
            logNewRelicPageAction(name, action);
        },
        [psErrorDetails]
    );

    let titleDisplay = '';
    let messageDisplay = '';
    let shouldLogError = false;
    let mappingExists = false;

    const getErrorDetails = (errorMessage: string) => {
        const [title, message] = parseMessageWithTitle(errorMessage);
        return {
            title: title,
            message: message,
            mappingFound: true,
            logError: true
        };
    };

    const getPaymentErrorDetails = useCallback(() => {
        if (failedAllowance || failedPayoff) {
            return {
                message: 'Including trade did not return a valid program.',
                mappingFound: false,
                logError: false
            };
        }
        if (failedMenuAccessories) {
            return {
                message: 'Adjustment did not return a payment.',
                mappingFound: false,
                logError: false
            };
        }
        if (isNoProgramIncentiveMessageEnabled && failedManualIncentive && !hasSelectedProgram) {
            return {
                message:
                    "This term doesn't have a lender program applied, so a manual incentive can't be priced. Select a program for this term, or turn off Auto Select.",
                mappingFound: false,
                logError: false
            };
        }
        // Step 1: Check PS_ERROR_MESSAGE_MAP for exact match
        if (psErrorDetails.lookupKey && PS_ERROR_MESSAGE_MAP[psErrorDetails.lookupKey]) {
            return {
                title: '',
                message: PS_ERROR_MESSAGE_MAP[psErrorDetails.lookupKey],
                mappingFound: true,
                logError: true
            };
        }

        if (
            psErrorDetails.lookupKey?.endsWith('_negativeamortization') &&
            NEGATIVE_AMORTIZATION_PS_ERROR_MESSAGE_MAP[currentOfferType]
        ) {
            return {
                title: '',
                message: NEGATIVE_AMORTIZATION_PS_ERROR_MESSAGE_MAP[currentOfferType],
                mappingFound: true,
                logError: true
            };
        }

        const lookupKeyWithPath = psErrorUtils.getPSErrorLookupKey(
            psErrorDetails.psErrorMessagePath,
            psErrorDetails.originalPSError
        );
        if (lookupKeyWithPath && PS_ERROR_MESSAGE_MAP[lookupKeyWithPath]) {
            return getErrorDetails(PS_ERROR_MESSAGE_MAP[lookupKeyWithPath]);
        }

        // Step 2: Check if this is an endpoint-specific error (PQ vs UPQ)
        if (psErrorDetails.lookupKey) {
            const psMessage = psErrorDetails.lookupKey.split('_')[1];
            const endpointKey = currentCalcEndpoint || CALC_ENDPOINTS.programQuotes;
            const endpointMap =
                isExplicitLenderP1Enabled && endpointKey === CALC_ENDPOINTS.programQuotes
                    ? PS_ERROR_MAP_PROGRAM_QUOTES_ASP_HIDDEN
                    : PS_ERROR_MAP_BY_CALC_ENDPOINT[endpointKey];
            if (endpointMap?.[psMessage]) {
                return {
                    title: '',
                    message: endpointMap[psMessage],
                    mappingFound: true,
                    logError: true
                };
            }
        }

        // Step 3: Check if we should use the server message (pass-through map)
        if (psErrorDetails.lookupKey) {
            const psMessage = psErrorDetails.lookupKey.split('_')[1];
            if (PS_ERROR_PASS_THROUGH_KEYS.has(psMessage)) {
                return {
                    title: '',
                    message: psErrorDetails.originalPSError,
                    mappingFound: true,
                    logError: true
                };
            }
        }

        // Step 4: Check BALLOON_PS_ERROR_MESSAGE_MAP for exact match
        if (currentOfferType === BALLOON && psErrorDetails.lookupKey) {
            const psMessage = psErrorDetails.lookupKey.split('_')[1];
            if (BALLOON_PS_ERROR_MESSAGE_MAP[psMessage]) {
                return {
                    title: '',
                    message: BALLOON_PS_ERROR_MESSAGE_MAP[psMessage],
                    mappingFound: true,
                    logError: true
                };
            }
        }

        const fallbackMessage = psErrorDetails.lookupKey && getPSErrorFallbackMessage(psErrorDetails.lookupKey);

        if (fallbackMessage) {
            return getErrorDetails(fallbackMessage);
        }

        return {
            message: 'Service Unavailable. Please try again.',
            mappingFound: false,
            logError: !!psErrorDetails.lookupKey
        };
    }, [
        failedAllowance,
        failedPayoff,
        psErrorDetails,
        PS_ERROR_MESSAGE_MAP,
        failedMenuAccessories,
        failedManualIncentive,
        hasSelectedProgram,
        isNoProgramIncentiveMessageEnabled,
        currentCalcEndpoint,
        currentOfferType,
        isExplicitLenderP1Enabled
    ]);

    if (isPaymentError) {
        const { title, message, mappingFound, logError } = getPaymentErrorDetails();
        titleDisplay = title || '';
        messageDisplay = message;
        mappingExists = mappingFound;
        shouldLogError = logError;
    } else if (displayedMessages.length === 1) {
        const [{ message }] = displayedMessages;
        messageDisplay = message;
    } else if (displayedMessages.length > 1) {
        messageDisplay = displayedMessages.map(({ message }, index) => (index ? ' ' : '') + message).toString();
    }

    if (shouldLogError) {
        logPSErrorToNR('MV: PSUserFriendlyErrors', messageDisplay, mappingExists);
    }

    let alertType: AlertSeverity | null = null;

    if (isPaymentError && lastPaymentMissingUPQFields && upqMissingFieldsMessage) {
        alertType = 'error';
        titleDisplay = '';
        messageDisplay = upqMissingFieldsMessage;
    } else if (isPaymentError) {
        alertType = 'error';
    } else if (paymentHasMessages) {
        alertType = 'info';
    }

    if (!alertType) {
        return null;
    }

    return (
        <Alert
            data-testid={testId}
            title={showMultipleErrorsTitle ? 'Multiple Errors Found' : ''}
            role="payment-status"
            id="payment-status"
            type={alertType}
            sx={{ mb: 2, ...sxOverrides }}
        >
            {titleDisplay && (
                <Typography tag="span" variant="h6" data-testid="payment-error-title" sx={{ display: 'block' }}>
                    {titleDisplay}
                </Typography>
            )}
            <Typography tag="span" variant="body-sm" data-testid="payment-error-message">
                {messageDisplay}
            </Typography>
        </Alert>
    );
};

export default PaymentStatusInterstate;
